import { useAuth } from '@hostesa/auth-client';
import { FeatureFlagProvider } from '@hostesa/feature-flags';
import { useT } from '@hostesa/i18n';
import { LoadingState } from '@hostesa/ui';
import { useEffect, useMemo } from 'react';
import { findOperatorMembership } from '../lib/operator';
import { useRuntime } from '../lib/runtime-context';
import { NoAccessPage } from '../routes/standalone-pages';
import { OperatorLayout } from './operator-layout';

/**
 * Operator gate for the whole backoffice. Unlike the platform's HotelGuard
 * (which resolves `:tenantSlug/:hotelSlug`), the backoffice is operator-level:
 *
 *   1. Wait for the session restore (GET /me) to finish.
 *   2. Find a `hostesa_admin` membership. NO operator membership → opaque
 *      no-access (never reveals tenant existence).
 *   3. Bind that membership as the ACTIVE one so `useAuth().can(...)` evaluates
 *      operator permissions (`admin:cross_tenant_read`, `admin:impersonate`,
 *      `usage:read`, ...) — never a hotel-user role.
 *   4. Compose the flag provider + operator layout.
 *
 * Per-route RBAC (cross-tenant read, etc.) and the provider-diagnostics flag
 * gate are applied inside <OperatorRoute>. The backend remains authoritative.
 */
export function OperatorGuard() {
  const { t } = useT('common');
  const { memberships, loading, setActiveMembershipFilter } = useAuth();
  const runtime = useRuntime();

  const operatorMembership = useMemo(() => findOperatorMembership(memberships), [memberships]);

  // Bind the operator membership so useAuth().can() targets the hostesa_admin
  // role. Never reuse a hotel-user membership for backoffice authorization.
  useEffect(() => {
    if (operatorMembership) {
      setActiveMembershipFilter({
        tenantId: operatorMembership.tenantId,
        ...(operatorMembership.hotelId ? { hotelId: operatorMembership.hotelId } : {}),
      });
    }
    return () => setActiveMembershipFilter(null);
  }, [operatorMembership, setActiveMembershipFilter]);

  // No per-hotel context in the backoffice; resolve env/default flags only.
  const flags = useMemo(() => runtime.flagEvaluator.resolve(), [runtime.flagEvaluator]);

  if (loading) {
    return <LoadingState data-testid="operator-loading" title={t('states.loadingTitle')} />;
  }

  // Operator gate: a non-hostesa_admin session sees the opaque no-access state.
  if (!operatorMembership) {
    return <NoAccessPage />;
  }

  return (
    <FeatureFlagProvider flags={flags}>
      <OperatorLayout />
    </FeatureFlagProvider>
  );
}
